@if (errorMessage) {
  <tidy-error-message
    [text]="errorMessage" />
}

<mat-menu #filterMenu="matMenu" class="filter-menu">
  <mat-tree [dataSource]="dataSource" [treeControl]="treeControl">
    <mat-tree-node *matTreeNodeDef="let node">
      <button mat-menu-item matTreeNodeToggle class="tree-node-padding">
        <mat-checkbox
          class="checkbox"
          [checked]="getNodeChecked(node)"
          (click)="$event.stopPropagation()"
          (change)="checkNode(node, $event)">
          {{ node.name }}
        </mat-checkbox>
      </button>
    </mat-tree-node>
    <mat-tree-node
      *matTreeNodeDef="let node; when: hasChild">
      <button
        class="node-button-padding"
        mat-menu-item
        matTreeNodeToggle
        [attr.aria-label]="'Toggle ' + node.name">
        <mat-icon class="mat-icon-rtl-mirror">
          {{ treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right' }}
        </mat-icon>
        {{ node.name | translate }}
      </button>
    </mat-tree-node>
  </mat-tree>
</mat-menu>

<ng-template #chipList>
  @for (chip of chips; track chip.parentName + chip.value) {
    <ion-chip class="filter-chip">
      @if (chip.icon) {
        <mat-icon
          class="chip-icon"
          [style.color]="chip.color || 'inherit'">{{ chip.icon }}</mat-icon>
      }
      <ion-label>{{ chip.name }}</ion-label>
      <ion-icon name="close-circle" (click)="removeChip(chip)" />
    </ion-chip>
  }
</ng-template>

<ng-template #filterAndDate>
  <tidy-small-button
    [text]="'Filter'"
    [image]="'assets/img/filter-white.svg'"
    [matMenuTriggerFor]="filterMenu"
    [matMenuTriggerDisabled]="showAboutThisPage"
    [matMenuTriggerData]="{closeDisabled: true}" />
  <form [formGroup]="dateRangeForm" novalidate action="#" class="form-filters">
    <tidy-header-datepicker
      (customDateOption)="selectCustomDateRange($event)"
      (stringDateOption)="selectStringDateRange($event)"
      [(dateRangeForm)]="dateRangeForm"
      [selectOptions]="dateRangeOptions"
      [rangeOption]="dateRangeOption"
      [icon]="'assets/svg/calendar-outline.svg'"
      [header]="true" />
  </form>
</ng-template>

<ng-template #headerButtons>
  <tidy-small-button
    [text]="'Add Transaction'"
    [image]="'assets/img/add-button-white.svg'"
    (buttonClick)="openAddTransaction()" />
  @if (!isDataLoaded || connectedAccounts.length > 0) {
    <tidy-small-button
      [text]="'Accounts'"
      [image]="'assets/svg/business-outline.svg'"
      (buttonClick)="openManageConnections()" />
  } @else {
    <tidy-small-button
      [text]="connecting ? 'Connecting…' : 'Add Account'"
      [image]="'assets/img/add-button-white.svg'"
      [disabled]="connecting"
      (buttonClick)="addAccount()" />
  }
  <tidy-small-button
    [text]="'Rules'"
    [image]="'assets/svg/options-outline.svg'"
    (buttonClick)="openRules()" />
</ng-template>

<ng-template #attentionToggle>
  <!-- Only a month opened from a P&L cell reads differently on accrual and cash; a plain date range does not. -->
  @if (yearMonth) {
    <tidy-month-attribution-switch
      [value]="attribution()"
      (valueChange)="setAttribution($event)" />
  }
  <div class="finance-filter-row__toggle">
    <tidy-toggle
      [toggled]="needsAttentionOnly"
      (toggleChanged)="toggleNeedsAttention($event)" />
    <tidy-text [body]="'Needs attention only'" />
  </div>
</ng-template>

@if (windowService.isDesktopRes) {
  <tidy-desktop-header #transactionsHeader>
    <div class="wrappable-container allow-wrap">
      <tidy-text
        [header]="'Transactions'" />
      <tidy-image
        src="assets/img/help-circle-outline.svg"
        class="body-size"
        [matTooltip]="'Income and expenses that feed your Monthly P&L. Bank transactions are sorted in automatically; the ones TIDY could not place show up under Needs attention.' | translate"
        [matTooltipPosition]="'below'"
        matTooltipClass="menu-item-tooltip below"
        style="opacity: 0.7" />
      <ng-container *ngTemplateOutlet="filterAndDate" />
      <ng-container *ngTemplateOutlet="headerButtons" />
      <tidy-image
        [src]="'assets/img/download_csv.svg'"
        class="desktop-header-icon secondary-text-icon"
        (action)="exportCsv()" />
      @if (!showAboutThisPage) {
        <ng-container *ngTemplateOutlet="attentionToggle" />
      }
      <ng-container *ngTemplateOutlet="chipList" />
    </div>
    <div class="non-wrappable-right-aligned-container">
      <automations-preview-button
        [tag]="'transactions'" />
    </div>
  </tidy-desktop-header>
  <!-- Skeleton table while loading — rows float in with the entrance cascade;
       the real table below then replaces it in place, instantly (skipEntrance). -->
  @if (!isDataLoaded) {
    <div class="desktop-flex-fill">
      <tidy-table
        [skeletonLoading]="true"
        [columnslabels]="headers" />
    </div>
  }
  @if (isDataLoaded && rows.length > 0) {
    <div class="desktop-flex-fill txn-table">
      <tidy-table
        #tidyTable
        [dataSource]="rows"
        [displayedColumns]="headers"
        [columnslabels]="headers"
        [allRowIsClickable]="true"
        [rowsToShow]="25"
        [showColumnLines]="false"
        [infiniteScroll]="true"
        [didLoadAllRows]="!hasMore"
        [isLoading]="loadingMore"
        [skipEntrance]="true"
        [blockSorting]="true"
        (pageChange)="loadMore()"
        (cellClick)="onTableColumnClick($event)"
        (selectChange)="onTableSelectChange($event)" />
    </div>
  }
  @if (isDataLoaded && rows.length === 0) {
    <tidy-alert>
      <tidy-row>
        <tidy-text [body]="emptyStateMessage" />
      </tidy-row>
      @if (showConnectPrompt) {
        <tidy-row class="extra-top-padding">
          <tidy-text
            [actionBody]="'Add Account'"
            (action)="addAccount()"
            class="link" />
        </tidy-row>
      }
    </tidy-alert>
  }
}

@if (!windowService.isDesktopRes) {
  <div style="display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; align-items: center;">
    <tidy-small-button
      class="mobile-filter-button"
      [iconOnly]="true"
      [image]="'assets/img/filter-white.svg'"
      [matMenuTriggerFor]="filterMenu"
      [matMenuTriggerDisabled]="showAboutThisPage" />
    <ng-container *ngTemplateOutlet="headerButtons" />
  </div>
  @if (!showAboutThisPage) {
    <div class="finance-filter-row">
      <form [formGroup]="dateRangeForm" novalidate action="#" class="form-filters">
        <tidy-header-datepicker
          (customDateOption)="selectCustomDateRange($event)"
          (stringDateOption)="selectStringDateRange($event)"
          [(dateRangeForm)]="dateRangeForm"
          [selectOptions]="dateRangeOptions"
          [rangeOption]="dateRangeOption"
          [icon]="'assets/svg/calendar-outline.svg'"
          [header]="true" />
      </form>
      <ng-container *ngTemplateOutlet="attentionToggle" />
    </div>
  }
  @if (chips.length > 0) {
    <div class="mobile-filter-chips">
      <ng-container *ngTemplateOutlet="chipList" />
    </div>
  }
  @if (!isDataLoaded) {
    @for (i of [1,2,3,4]; track i) {
      <div class="finance-skeleton sk-card">
        <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
          <ion-skeleton-text [animated]="true" style="width: 55%; height: 18px; border-radius: 4px" />
          <ion-skeleton-text [animated]="true" style="width: 18%; height: 18px; border-radius: 4px" />
        </div>
        <ion-skeleton-text [animated]="true" style="width: 40%; height: 12px; border-radius: 4px" />
      </div>
    }
  }
  @if (isDataLoaded) {
    @for (view of views; track view.txn.id) {
      <tidy-card (click)="openTransaction(view)">
        <tidy-row [alignLastItemRight]="true">
          <tidy-text
            [header]="view.txn.description || 'Transaction'"
            [translate]="false" />
          @if (view.needsAttention) {
            <div style="display: flex; align-items: center; gap: 4px;">
              <tidy-image
                [src]="'assets/img/alert-circle-outline-red.svg'"
                class="body-size" />
              <tidy-text
                [smallBody]="'Needs attention'"
                class="red" />
            </div>
          }
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text
            [helper]="view.dateLabel + (view.isUpcoming ? ' · Upcoming' : '')"
            [translate]="false" />
          <div class="txn-card-amount">
            <tidy-text
              [body]="view.formattedAmount"
              [translate]="false" />
            @if (view.wholeAmountNote) {
              <tidy-text
                [helper]="view.wholeAmountNote"
                [translate]="false" />
            }
          </div>
        </tidy-row>
        <tidy-row class="extra-top-padding">
          <tidy-text
            [smallBody]="(view.categoryLabel || (view.suggestionLabel ? 'Suggested: ' + view.suggestionLabel : 'No category')) + ' · ' + (view.propertyLabel || 'No property') + ' · ' + view.sourceLabel"
            [translate]="false" />
        </tidy-row>
        @if (view.job || view.reservation) {
          <tidy-row class="extra-top-padding txn-record-buttons" (click)="$event.stopPropagation()">
            @if (view.job) {
              <tidy-small-button
                [text]="view.job.label"
                [image]="view.job.icon"
                [colorImage]="true"
                (buttonClick)="openJob(view)" />
            }
            @if (view.reservation) {
              <tidy-small-button
                [text]="view.reservation.label"
                [image]="view.reservation.initials ? '' : view.reservation.icon"
                [colorImage]="true"
                [initials]="view.reservation.initials"
                (buttonClick)="openReservation(view)" />
            }
          </tidy-row>
        }
      </tidy-card>
    }
    @if (views.length === 0) {
      <tidy-alert>
        <tidy-row>
          <tidy-text [body]="emptyStateMessage" />
        </tidy-row>
      </tidy-alert>
    }
    @if (hasMore) {
      <tidy-button
        [text]="loadingMore ? 'Loading…' : 'Load More'"
        [action]="boundLoadMore"
        [disabled]="loadingMore"
        class="secondary" />
    }
  }
}

@if (showAboutThisPage) {
  <tidy-about-this-page [page]="'transactions'" [overlayTop]="overlayTop" (dismissed)="dismissAboutThisPage()" />
}
